import React, { memo } from 'react';
import { ArrowRight, Eye, Waves, Compass, MessagesSquare, Sparkles } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useScreenInit } from '../useScreenInit';
const pillars = [{
  icon: Eye,
  title: 'Self-Awareness',
  body: 'The ability to notice your own internal world — your feelings, reactions, values, and patterns — with curiosity instead of judgment.'
}, {
  icon: Waves,
  title: 'Emotional Regulation',
  body: 'The capacity to respond instead of react. This protects your relationships and gives you room to actually hear other people.'
}, {
  icon: Compass,
  title: 'Motivation',
  body: "Understanding what's been getting in the way of your goals — especially when anxiety or depression have made old habits hard to break."
}, {
  icon: MessagesSquare,
  title: 'Communication',
  body: 'Clearer access to your own thoughts and feelings makes it possible to ask for what you need, and to actually be understood.'
}];
export function IndividualTherapy() {
  useScreenInit();
  return <div className="min-h-screen flex flex-col" data-id="element-214" data-mp="13fl57b33l86e">
      <Navbar data-id="element-215" data-mp="187cp52rr0fnk" />
      <main className="flex-grow" data-id="element-216" data-mp="187mh2vwcswvw">
        {/* Hero / Intro */}
        <section className="relative pt-24 pb-20 overflow-hidden" data-id="element-217" data-mp="1d6zdo8d11ibo">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-218" data-mp="1d67havblvgkc">
            <p className="text-sm font-semibold uppercase tracking-widest text-accent-500 mb-4" data-id="element-219" data-mp="fkihj6g15ari">
              Therapy Services
            </p>
            <h1 className="text-5xl md:text-6xl font-title font-bold text-stone-900 leading-tight mb-8" data-id="element-220" data-mp="phei8pzossli">
              Individual Therapy
            </h1>
            <h2 className="text-2xl md:text-3xl font-title font-bold text-brand-700 mb-8" data-id="element-221" data-mp="uh98crbn1e5i">
              What to Expect from Individual Therapy
            </h2>
            <div className="space-y-6 text-lg text-stone-600 leading-relaxed" data-id="element-222" data-mp="25veik22g2k9a">
              <p data-id="element-223" data-mp="25xhcuyamqzjt">
                Therapy isn't only for crisis moments. It's for anyone who wants
                more self-knowledge, better tools for stress, or a clearer sense
                of who they are in relationship to others.
              </p>
              <p data-id="element-224" data-mp="essad1c5hpjx">
                My approach is grounded in Affect Relational Therapy. We don't
                just talk about what's going on. We slow down enough to notice
                what you're actually feeling in the moment, and why old patterns
                of relating to yourself and to others might no longer be serving
                you. From there, we build something healthier.
              </p>
            </div>
          </div>
          <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[700px] h-[700px] bg-brand-100 rounded-full blur-3xl opacity-40 -z-10" data-id="element-225" data-mp="3n9wp70h5rgkp" />
        </section>

        {/* Relationship Animals callout */}
        <section className="py-12" data-id="element-226" data-mp="2vjijvsn7nckn">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227" data-mp="2vi7ddl2drqpu">
            <div className="bg-brand-800 text-white rounded-3xl p-8 md:p-12" data-id="element-228" data-mp="2leijdasp4sz4">
              <div className="flex items-start gap-5" data-id="element-229" data-mp="2q58tf1c2hk05">
                <div className="hidden sm:flex w-12 h-12 shrink-0 bg-white/10 rounded-2xl items-center justify-center text-brand-100" data-id="element-230" data-mp="2q6tn6oqk4ywy">
                  <Sparkles className="h-6 w-6" data-id="element-231" data-mp="2gffscu9jwna5" />
                </div>
                <div data-id="element-232" data-mp="2gdl5vbimsn3y">
                  <p className="text-lg text-brand-50 leading-relaxed mb-6" data-id="element-233" data-mp="345a1p1unvtpn">
                    One tool I often bring to this work is the{' '}
                    <span className="font-semibold text-white" data-id="element-234" data-mp="3931vy2is3rx0">
                      Relationship Animals
                    </span>{' '}
                    framework, which comprises eight archetypes that describe
                    how people tend to connect, protect themselves, and love.
                    It's entirely optional — but I've found it gives people a
                    fast, memorable way to see their own patterns clearly, which
                    makes the harder work of change easier to start.
                  </p>
                  <a href="#" className="inline-flex items-center px-6 py-3 bg-accent-500 text-white rounded-full font-medium hover:bg-accent-600 transition-colors" data-id="element-235" data-mp="23zzgur8hhder">
                    Curious what your archetype is? Take the assessment
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-236" data-mp="1p7fmaezege8x" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Therapy Can Help You Build */}
        <section className="py-20" data-id="element-237" data-mp="2cykzemdygsfj">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-238" data-mp="2cxjl891n7ptl">
            <h2 className="text-3xl md:text-4xl font-title font-bold text-center mb-14" data-id="element-239" data-mp="2809pycrkojxu">
              What Therapy Can Help You Build
            </h2>
            <div className="grid sm:grid-cols-2 gap-6 lg:gap-8" data-id="element-240" data-mp="11kzc76rnp0wn">
              {pillars.map(pillar => {
              const Icon = pillar.icon;
              return <div key={pillar.title} className="bg-white rounded-3xl p-8 border border-accent-100 shadow-sm" data-id="element-241" data-mp="3899coctvzghy">
                    <div className="w-12 h-12 bg-brand-100 rounded-2xl flex items-center justify-center mb-6 text-brand-700" data-id="element-242" data-mp="3i5uk14tdp3oy">
                      <Icon className="h-6 w-6" data-id="element-243" data-mp="3mxmalfcjb9f3" />
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900 mb-3" data-id="element-244" data-mp="3rwp5i6ysvz9c">
                      {pillar.title}
                    </h3>
                    <p className="text-stone-600 leading-relaxed" data-id="element-245" data-mp="3vvpphst0l7xx">
                      {pillar.body}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20" data-id="element-246" data-mp="26jdzcgx7smir">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-247" data-mp="26kfdiu9j1p4p">
            <h2 className="text-3xl md:text-4xl font-title font-bold mb-8" data-id="element-248" data-mp="2bigxriiovuhr">
              Ready when you are.
            </h2>
            <a href="#" className="inline-flex items-center px-8 py-4 bg-accent-500 text-white rounded-full font-medium hover:bg-accent-600 transition-all hover:shadow-lg hover:-translate-y-0.5" data-id="element-249" data-mp="23j2ijc3jygek">
              Let's get started
              <ArrowRight className="ml-2 h-5 w-5" data-id="element-250" data-mp="28fb5vcrwlfwt" />
            </a>
          </div>
        </section>
      </main>
      <Footer data-id="element-251" data-mp="2meaoo3sqj0l2" />
    </div>;
}